import React, {
    useEffect,
    useId,
    useRef,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeadset } from '@fortawesome/free-solid-svg-icons';

import type Location from '@r6digital/rapidstor-types/react/entities/Location';

import ChevronLeft from '@/assets/icon-chevron-left.svg?react';
import useBodyScrollLock from '@/core/hooks/useBodyScrollLock';
import useFocusTrap from '@/core/hooks/useFocusTrap';

import {
    SpaceCalculatorMode,
    SpaceCalculatorResult,
    VEHICLE_STORAGE_RESULT,
} from './calculator';
import ModeSelector from './ModeSelector';
import {
    BusinessStorageForm,
    CustomNeedsForm,
    ExtraSpaceForm,
    MovingHouseForm,
} from './ModeForms';

import classes from './styles.module.scss';

function ModeTitle({ mode }: { mode: SpaceCalculatorMode }) {
    const { t } = useTranslation();

    switch (mode) {
        case 'movingHouse':
            return <>{t('standardUnitListing:spaceCalculator:movingHouse')}</>;
        case 'extraSpace':
            return <>{t('standardUnitListing:spaceCalculator:extraSpace')}</>;
        case 'businessStorage':
            return <>{t('standardUnitListing:spaceCalculator:businessStorage')}</>;
        case 'customNeeds':
            return <>{t('standardUnitListing:spaceCalculator:customNeeds')}</>;
        default:
            return <>{t('standardUnitListing:spaceCalculator:title')}</>;
    }
}

interface SpaceCalculatorModalProps {
    location: Location;
    isVehicleStorageAvailable: boolean;
    onClose: () => void;
    /** `null` when the customer submitted without moving any slider. */
    onFinish: (result: SpaceCalculatorResult | null) => void;
}

function SpaceCalculatorModal({
    location,
    isVehicleStorageAvailable,
    onClose,
    onFinish,
}: SpaceCalculatorModalProps) {
    const { t } = useTranslation();
    const titleId = useId();
    const [mode, setMode] = useState<SpaceCalculatorMode>('none');
    const values = location.rapidstor.spaceCalculatorValues;
    const dialogRef = useRef<HTMLDivElement>(null);
    const titleRef = useRef<HTMLDivElement>(null);
    const isFirstStep = useRef(true);

    useBodyScrollLock(true);
    useFocusTrap(dialogRef);

    // The button that changed the step unmounts with it, so focus would otherwise fall to the page.
    useEffect(() => {
        if (isFirstStep.current) {
            isFirstStep.current = false;
            return;
        }
        titleRef.current?.focus();
    }, [mode]);

    useEffect(() => {
        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };
        window.addEventListener('keydown', closeOnEscape);
        return () => window.removeEventListener('keydown', closeOnEscape);
    }, [onClose]);

    const returnToModeSelection = () => setMode('none');

    const renderStep = () => {
        switch (mode) {
            case 'movingHouse':
                return <MovingHouseForm values={values} onSubmit={onFinish} />;
            case 'extraSpace':
                return <ExtraSpaceForm values={values} onSubmit={onFinish} />;
            case 'businessStorage':
                return <BusinessStorageForm values={values} onSubmit={onFinish} />;
            case 'customNeeds':
                return (
                    <CustomNeedsForm
                        values={values}
                        sizeSymbol={location.rapidstor.sizeSymbol}
                        onSubmit={onFinish}
                    />
                );
            default:
                return (
                    <ModeSelector
                        isVehicleStorageAvailable={isVehicleStorageAvailable}
                        onSelectMode={setMode}
                        onSelectVehicleStorage={() => onFinish(VEHICLE_STORAGE_RESULT)}
                    />
                );
        }
    };

    return (
        <div
            className={classes.overlay}
            onClick={(event) => {
                if (event.target === event.currentTarget) {
                    onClose();
                }
            }}
            role="presentation"
        >
            <div
                ref={dialogRef}
                className={classes.dialog}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                data-testid="space-calculator"
            >
                <button
                    type="button"
                    className={classes.closeButton}
                    aria-label={t('cta:close')}
                    onClick={onClose}
                >
                    &times;
                </button>

                <aside className={classes.helpPanel}>
                    <FontAwesomeIcon icon={faHeadset} className={classes.supportIcon} />
                    <div className={classes.helpTitle}>
                        {t('standardUnitListing:spaceCalculator:needHelpTitle')}
                    </div>
                    <div className={classes.helpSubtitle}>
                        {t('standardUnitListing:spaceCalculator:weCanHelp')}
                    </div>
                    <button
                        type="button"
                        className={classes.findSpaceButton}
                        onClick={() => setMode('customNeeds')}
                    >
                        {t('standardUnitListing:spaceCalculator:findMeASpace')}
                    </button>
                    <p>
                        {t('standardUnitListing:spaceCalculator:orCall')}
                        {' '}
                        <span className={classes.phone}>{location.contactPhone}</span>
                    </p>
                    {mode !== 'none' && (
                        <button
                            type="button"
                            className={classes.previousStep}
                            onClick={returnToModeSelection}
                        >
                            <ChevronLeft aria-hidden="true" />
                            {t('standardUnitListing:spaceCalculator:previousStep')}
                        </button>
                    )}
                </aside>

                <div className={classes.mainPanel}>
                    {mode !== 'none' && (
                        <button
                            type="button"
                            className={classes.previousStepCompact}
                            aria-label={t('standardUnitListing:spaceCalculator:previousStep')}
                            onClick={returnToModeSelection}
                        >
                            <ChevronLeft aria-hidden="true" />
                        </button>
                    )}
                    <div className={classes.headings}>
                        <div id={titleId} ref={titleRef} tabIndex={-1} className={classes.title}>
                            <ModeTitle mode={mode} />
                        </div>
                        {mode === 'none' && (
                            <p className={classes.intro}>
                                {t('standardUnitListing:spaceCalculator:intro')}
                            </p>
                        )}
                    </div>
                    {renderStep()}
                </div>
            </div>
        </div>
    );
}

export default SpaceCalculatorModal;
